import { styled } from '@linaria/react';
import { useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';

import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { centerUnderPointer } from '@atlaskit/pragmatic-drag-and-drop/utils/center-under-pointer';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/utils/set-custom-native-drag-preview';
import { ScrollArea, Skeleton, Stack } from '@mantine/core';
import type { OrganizationStatusesByProjectsAndBoards } from '../../../../../api/organization-client/organization-client.type';
import type { Workflow } from '../../../../../api/workflows-client/workflows-client.type';
import { useTranslation } from 'react-i18next';
import { useStatusLabels } from '../../../../../contexts/status-labels.context';
import { normalizeStatusName } from '../../../../../helpers/subgroup-helpers/subgroup-helpers';
import { baseWhite, primaryBase, secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import {
  filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds,
  getMappedAndUnmappedStatusesForWorkflow,
  maybeRemoveMappedStatusesFromWorkflowSteps,
} from '../workflows.helpers';
import { WorkflowAction } from './reducer';

/**
 * The "Active Statuses" column: every status on the workflow's boards that is
 * not yet mapped to a step, rendered as draggable chips and doubling as the
 * drop target that un-maps a status dragged back out of a step.
 *
 * Chips are ordered by their displayed label rather than their raw value, since
 * once boards report mixed casing the two orderings diverge.
 *
 * @param {Workflow} state - The workflow being viewed or edited
 * @param {(action: WorkflowAction) => void} dispatch - Reducer dispatch for workflow edits
 * @param {OrganizationStatusesByProjectsAndBoards | undefined} statusesByProjectsAndBoards - Org statuses, undefined until loaded
 * @param {boolean} isEditable - Whether chips can be dragged
 * @returns {JSX.Element} The unmapped-status list and its drop target
 */
export function Statuses({
  state,
  dispatch,
  statusesByProjectsAndBoards,
  isEditable,
}: {
  state: Workflow;
  dispatch: (action: WorkflowAction) => void;
  statusesByProjectsAndBoards: OrganizationStatusesByProjectsAndBoards | undefined;
  isEditable: boolean;
}) {
  const i18n = useTranslation();
  // instead of boolean, use a string to record the name of the status that is being dragged over
  // null means no status is being dragged over
  const [draggedOver, setDraggedOver] = useState<string | null>(null);
  const ref = useRef(null);
  const { getStatusLabel } = useStatusLabels();

  const statusesForWorkflowSubprojects = filterOrganizationStatusesByProjectsAndBoardsBySubprojectIds(
    statusesByProjectsAndBoards,
    state.subprojects,
  );
  const { unmappedStatuses } = getMappedAndUnmappedStatusesForWorkflow(state, statusesForWorkflowSubprojects);

  // Ordered by the label the user actually reads, not by the underlying value.
  // A default sort on the value would interleave the list once casing varies,
  // because ASCII orders every uppercase letter before every lowercase one.
  // Copied first — the raw array is closed over by the drop handler below.
  const unmappedStatusesInDisplayOrder = [...unmappedStatuses].sort((a, b) =>
    getStatusLabel(a).localeCompare(getStatusLabel(b)),
  );

  useEffect(() => {
    const element = ref.current;
    invariant(element);

    return dropTargetForElements({
      element,
      onDragEnter: ({ source }: { source: { data: Record<string, any> } }) => {
        setDraggedOver(source.data.status as string);
      },
      onDragLeave: () => {
        setDraggedOver(null);
      },
      onDrop: ({
        source,
      }: {
        source: { data: Record<string, any> };
        location: { current: { dropTargets: any[] } };
      }) => {
        // step 1: check if the status is already in the unmappedStatuses array.
        // Compared on the normalized name so a casing difference between the
        // drag payload and the board's status list doesn't read as a new status.
        const normalizedDraggedStatus = normalizeStatusName(source.data.status as string);
        if (unmappedStatuses.some((status) => normalizeStatusName(status) === normalizedDraggedStatus)) {
          setDraggedOver(null);
          return;
        }
        // step 2: if the status is not in the unmappedStatuses array, remove the status from the mappedStatuses in the workflow
        const newSteps = maybeRemoveMappedStatusesFromWorkflowSteps(state, [source.data.status as string]);
        // step 3: update the workflow
        dispatch({
          type: 'SET_WORKFLOW',
          payload: { ...state, steps: newSteps },
        });
        setDraggedOver(null);
      },
    });
  }, [unmappedStatuses, state, dispatch]);

  return (
    <Stack h="100%">
      <Text weight="bold">
        {i18n.t('data_management.workflows.active_statuses', {
          active: i18n.t('common.active'),
          statuses: i18n.t('common.status.statuses'),
        })}
      </Text>
      <ScrollArea mb="md" style={{ flex: 1 }} type="auto">
        <Stack
          ref={ref}
          gap="xs"
          p="md"
          style={{
            border: draggedOver ? `2px solid ${primaryBase}` : 'none',
            borderRadius: 16,
          }}
        >
          {unmappedStatusesInDisplayOrder.length > 0 ? (
            <>
              {/* Render all statuses without any skeletons */}
              {unmappedStatusesInDisplayOrder.map((status) => (
                <Status key={status} status={status} isDraggable={isEditable} />
              ))}

              {/* Only add a single skeleton if something is being dragged over */}
              {draggedOver && <Skeleton key={`drag-preview-${draggedOver}`} height={32} radius={8} />}
            </>
          ) : (
            <Text color={skyDark} size="small" style={{ textAlign: 'left', padding: '0' }}>
              {i18n.t('data_management.workflows.add_boards_to_view_statuses', {
                boards: i18n.t('common.boards'),
                statuses: i18n.t('common.status.statuses'),
              })}
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

/**
 * Renders one draggable status chip.
 *
 * `status` is the value: it is what gets persisted as a step's
 * `external_name` and what the drag payload carries. The text shown to the
 * user is resolved separately from the board's source-cased status names, so
 * the chip reads "Code Review" while still mapping the stored `code review`.
 * Outside a `StatusLabelsProvider` the label falls back to `status` verbatim.
 *
 * @param {string} status - The raw status value from the board or an existing mapping
 * @param {string} [backgroundColor] - Chip fill; defaults to the secondary token
 * @param {boolean} isDraggable - Whether the chip can be dragged between steps
 */
export function Status({
  status,
  backgroundColor,
  isDraggable,
}: {
  status: string;
  backgroundColor?: string;
  isDraggable: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null!);
  const [dragging, setDragging] = useState<boolean>(false);
  const { getStatusLabel } = useStatusLabels();
  const label = getStatusLabel(status);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    try {
      const cleanup = draggable({
        element,
        canDrag: () => {
          return isDraggable;
        },
        getInitialData: () => {
          return { status, type: 'status' };
        },
        // Infer `nativeSetDragImage` from the v2 adapter's onGenerateDragPreview type
        // (DataTransfer['setDragImage'] | null) so it matches setCustomNativeDragPreview.
        // The prior hand-written annotation mirrored the now-removed v1 type shim.
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          try {
            let parentEl = element.parentElement;
            const stackingContext = [];
            while (parentEl) {
              const computedStyle = window.getComputedStyle(parentEl);
              stackingContext.push({
                tagName: parentEl.tagName,
                id: parentEl.id || 'no-id',
                className: parentEl.className || 'no-class',
                zIndex: computedStyle.zIndex,
                position: computedStyle.position,
                pointerEvents: computedStyle.pointerEvents,
              });
              parentEl = parentEl.parentElement;
            }

            setCustomNativeDragPreview({
              getOffset: centerUnderPointer,
              render: ({ container }: { container: HTMLElement }) => {
                // Create a clone of the element with proper styling
                const preview = document.createElement('div');

                // Apply the same styles as your original component
                Object.assign(preview.style, {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderRadius: '8px', // Ensure rounded corners
                  backgroundColor: backgroundColor || secondaryBase,
                  color: baseWhite,
                  minWidth: `${element.offsetWidth}px`,
                  zIndex: '9999',
                  pointerEvents: 'none',
                });

                // Add the content — display label, not the stored value
                preview.textContent = label;

                // Add the preview to the container
                container.appendChild(preview);
              },
              nativeSetDragImage,
            });
          } catch (error) {
            console.error('Error setting custom drag preview:', error);
          }

          return true;
        },
        onDragStart: () => {
          setDragging(isDraggable);
        },
        onDrop: () => {
          setDragging(false);
        },
      });

      return () => {
        cleanup();
      };
    } catch (error) {
      console.error('Error setting up draggable for status:', status, error);
      return undefined;
    }
  }, [status, label, isDraggable, backgroundColor]);

  return (
    <FlexContainer
      ref={ref}
      backgroundColor={backgroundColor}
      isDraggable={isDraggable}
      style={{
        visibility: dragging ? 'hidden' : 'visible',
        opacity: dragging ? 0 : 1,
      }}
    >
      <Text color={baseWhite}>{label}</Text>
      <Icon name="drag_indicator" size={16} color={baseWhite} style={{ cursor: isDraggable ? 'grab' : 'default' }} />
    </FlexContainer>
  );
}

const FlexContainer = styled.div<{
  backgroundColor?: string;
  isDraggable: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border-radius: 8px;
  background-color: ${({ backgroundColor }) => backgroundColor || secondaryBase};
  &:hover {
    cursor: ${({ isDraggable }) => (isDraggable ? 'grab' : 'default')};
  }
`;
